import {Alert, Attributes, Card, openDialog} from 'platform/components';
import {Grid, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNil, isNotNil, uniq} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  type ConfigurationDetailResponseBody,
  type PriceSnapshotLineResponseBody,
  useGetCatalogueTreeQuery,
  useGetConfiguratorBundleQuery,
  useListTrimFeatureAssignmentsQuery,
} from '@dms/api/new-vehicle';
import {ConfigurationCustomerView, useNewVehicleCatalogueLabels} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';
import {is404ApiError, usePermissions} from '@dms/shared';

import {
  ColourSwatch,
  type ColourSwatchProps,
} from '../../../../components/ColourSwatch/ColourSwatch';
import {PriceTag} from '../../../../components/PriceTag/PriceTag';
import {findCatalogueModelYear} from './findCatalogueModelYear';
import {getStandardEquipment} from './getStandardEquipment';
import {StandardEquipmentCard} from './StandardEquipmentCard';

const t = (key: string, defaultValue: string) => i18n.t(key, {defaultValue});
const SEPARATOR = ' · ';

const lookRow = (
  label: string,
  line: PriceSnapshotLineResponseBody | undefined,
  currency: string,
  hex: ColourSwatchProps['hex']
) =>
  isNil(line)
    ? {label, value: null}
    : {
        label,
        content: (
          <HStack spacing={2} align="center">
            <ColourSwatch name={line.label} hex={hex} size="dot" />
            <Text size="xSmall">{line.label}</Text>
            <PriceTag amount={line.unitNet} currency={currency} size="xSmall" />
          </HStack>
        ),
      };

export function ConfigurationTab(props: {detail: ConfigurationDetailResponseBody}) {
  const configuration = props.detail.configuration;
  const snapshot = props.detail.currentPriceSnapshot;
  const selection = configuration.selection;
  const labels = useNewVehicleCatalogueLabels();
  // The catalogue endpoints need catalogue read on top of the configuration read this page needs.
  const [canReadCatalogue] = usePermissions({permissionKeys: ['newVehicleCatalogueRead']});
  const treeQuery = useGetCatalogueTreeQuery({}, {skip: !canReadCatalogue});
  const bundleQuery = useGetConfiguratorBundleQuery(
    {modelYearId: selection.modelYearId},
    {skip: !canReadCatalogue}
  );
  const assignmentsQuery = useListTrimFeatureAssignmentsQuery(
    {trimId: selection.trimId},
    {skip: !canReadCatalogue}
  );
  const bundle = bundleQuery.currentData;
  const assignments = assignmentsQuery.currentData;
  const catalogue = findCatalogueModelYear(treeQuery.currentData, selection.modelYearId);
  const trim = bundle?.trims.find(({id}) => id === selection.trimId);
  const variant = bundle?.variants.find(({id}) => id === selection.variantId);
  const engine = bundle?.engines.find(({id}) => id === variant?.engineId);
  const linesOfType = (type: string) => snapshot.lines.filter((line) => line.type === type);
  const featureLines = linesOfType('feature');
  const optionLines = [...linesOfType('package'), ...featureLines];
  const equipmentState = match({
    canReadCatalogue: Boolean(canReadCatalogue),
    // The public bundle drops unpublished trims, and both endpoints 404 once they are archived.
    isGone:
      is404ApiError(bundleQuery.error) ||
      is404ApiError(assignmentsQuery.error) ||
      (isNotNil(bundle) && isNil(trim)),
    isError: bundleQuery.isError || assignmentsQuery.isError,
  })
    .with({canReadCatalogue: false}, () => 'noAccess' as const)
    .with({isGone: true}, () => 'unavailable' as const)
    .with({isError: true}, () => 'error' as const)
    .otherwise(() => 'ready' as const);
  const standardEquipment =
    equipmentState === 'ready' && bundle && assignments
      ? getStandardEquipment(assignments, bundle, {
          ...selection,
          // Snapshot feature lines include what the configurator added automatically.
          featureIds: uniq([...selection.featureIds, ...featureLines.map(({id}) => id)]),
        })
      : [];
  const vehicleName = catalogue
    ? [catalogue.make.name, catalogue.model.name, trim?.name].filter(isNotNil).join(' ')
    : configuration.label;

  return (
    <VStack spacing={4}>
      <Show when={props.detail.catalogueChanged}>
        <Alert
          variant="info"
          message={t(
            'entity.newVehicle.vehicleDetail.catalogueChangedHint',
            'The catalogue changed after this configuration was saved. Equipment and specification show the current catalogue; prices stay frozen.'
          )}
        />
      </Show>
      <Card
        title={vehicleName}
        subtitle={[catalogue?.modelYear.name, engine?.name].filter(isNotNil).join(SEPARATOR)}
        actions={[
          {
            type: 'button',
            variant: 'secondary',
            leftIcon: 'action/visibility',
            title: t('entity.newVehicle.actions.viewAsCustomer', 'View as customer'),
            onClick: () =>
              openDialog(<ConfigurationCustomerView configurationId={configuration.id} />, {
                size: 'large',
                title: t('entity.newVehicle.labels.customerView', 'Customer view'),
              }),
          },
        ]}
        data-testid="new-vehicle-configuration"
      >
        <Grid columns={[1, 1, 2, 2]} spacing={6}>
          <VStack spacing={3}>
            <Heading size={5}>
              {t('entity.newVehicle.vehicleDetail.selectedConfiguration', 'Selected configuration')}
            </Heading>
            <Attributes
              rows={[
                {
                  label: i18n.t('entity.vehicle.labels.model'),
                  value: catalogue ? `${catalogue.make.name} ${catalogue.model.name}` : null,
                },
                {
                  label: t('entity.newVehicle.labels.trimLevel', 'Trim level'),
                  value: trim?.name ?? linesOfType('variant').at(0)?.label,
                },
                {
                  label: t('entity.newVehicle.labels.modelYear', 'Model year'),
                  value: catalogue?.modelYear.name,
                },
                {
                  label: t('entity.newVehicle.catalogue.engine', 'Engine'),
                  value: engine?.name,
                },
                lookRow(
                  t('entity.newVehicle.labels.colour', 'Colour'),
                  linesOfType('colour').at(0),
                  snapshot.currency,
                  bundle?.colours.find(({id}) => id === selection.colourId)?.hex
                ),
                lookRow(
                  t('entity.newVehicle.labels.upholstery', 'Upholstery'),
                  linesOfType('upholstery').at(0),
                  snapshot.currency,
                  bundle?.upholsteries.find(({id}) => id === selection.upholsteryId)
                    ?.interiorColorHex
                ),
              ]}
            />
          </VStack>
          <Show when={engine}>
            {(engine) => (
              <VStack spacing={3}>
                <Heading size={5}>
                  {t(
                    'entity.newVehicle.vehicleDetail.technicalSpecification',
                    'Technical specification'
                  )}
                </Heading>
                <Attributes
                  rows={[
                    {
                      label: t('entity.newVehicle.catalogue.power', 'Power'),
                      value: `${Number(engine.powerKw)} kW`,
                    },
                    {
                      label: t('entity.newVehicle.catalogue.fuel', 'Fuel'),
                      value: labels.formatFuel(engine),
                    },
                    {
                      label: t('entity.newVehicle.catalogue.transmission', 'Transmission'),
                      value: labels.getTransmission(engine.transmission),
                    },
                    {
                      label: t('entity.newVehicle.catalogue.drive', 'Drive'),
                      value: labels.getDrive(engine.drive),
                    },
                    {
                      label: t('entity.newVehicle.catalogue.bodyStyle', 'Body style'),
                      value: trim ? labels.getBodyStyle(trim.bodyStyle) : null,
                    },
                    {
                      label: t('entity.newVehicle.catalogue.orderCode', 'Order code'),
                      value: variant?.orderCode,
                    },
                  ]}
                />
              </VStack>
            )}
          </Show>
        </Grid>
      </Card>

      <Card
        title={t('entity.newVehicle.steps.optionsPackages', 'Options & packages')}
        subtitle={t(
          'entity.newVehicle.vehicleDetail.optionsPricesHint',
          'Prices excl. VAT, as frozen with the configuration'
        )}
        data-testid="new-vehicle-options"
      >
        {optionLines.length === 0 ? (
          <Text size="small" color="tertiary">
            {t('entity.newVehicle.labels.noneSelected', 'None selected')}
          </Text>
        ) : (
          <VStack spacing={3}>
            {optionLines.map((line) => {
              const members = bundle?.packages
                .find(({id}) => id === line.id)
                ?.memberFeatureIds.flatMap(
                  (featureId) => bundle.features.find(({id}) => id === featureId)?.name ?? []
                );
              return (
                <HStack key={`${line.type}-${line.id}`} justify="space-between" spacing={4}>
                  <VStack spacing={1} minWidth={0}>
                    <HStack spacing={2} align="center">
                      <Icon
                        value={line.type === 'package' ? 'action/view_module' : 'content/add'}
                        size={4}
                        color="text.secondary"
                      />
                      <Text size="small" alternative>
                        {line.label}
                      </Text>
                    </HStack>
                    <Show when={isNotNilOrEmpty(members) ? members : null}>
                      {(members) => (
                        <Text size="xSmall" color="tertiary">
                          {members.join(SEPARATOR)}
                        </Text>
                      )}
                    </Show>
                  </VStack>
                  <PriceTag amount={line.unitNet} currency={snapshot.currency} />
                </HStack>
              );
            })}
          </VStack>
        )}
      </Card>

      <StandardEquipmentCard
        state={equipmentState}
        groups={standardEquipment}
        isLoading={bundleQuery.isLoading || assignmentsQuery.isLoading}
        onRetry={() => {
          void bundleQuery.refetch();
          void assignmentsQuery.refetch();
        }}
      />
    </VStack>
  );
}
